import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { playBellSound, stopBellSound } from "@/lib/bellSound";
import { autoPrintOrder, connectQZTray } from "@/utils/printService";
import { useSearchParams } from "react-router-dom";

export function useOrders() {
  const { authAxios, authReady } = useAuth();
  const authAxiosRef = useRef(authAxios); authAxiosRef.current = authAxios;
  const [orders, setOrders] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [cashRegister, setCashRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get("id");

  const prevOrderIdsRef = useRef(new Set());
  const alertIntervalRef = useRef(null);
  const isFirstLoad = useRef(true);
  const printStationsRef = useRef([]);
  const restaurantRef = useRef(null);
  // Always points to latest fetchData — lets polling/WS effects avoid stale closures
  // without adding fetchData (or authAxios) to their dependency arrays.
  const fetchDataRef = useRef(null);
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    if (!deepLinkId || deepLinkHandled.current || !orders.length) return;
    const found = orders.find(o => o.id === deepLinkId);
    if (found) { setSelectedOrder(found); deepLinkHandled.current = true; }
  }, [deepLinkId, orders]);

  useEffect(() => {
    if (!authReady) return;
    const savedPrinter = localStorage.getItem("kaiso_selected_printer");
    if (savedPrinter) {
      connectQZTray({ manual: false }).catch(() => {});
    }
    authAxiosRef.current.get("/restaurant").then(r => {
      restaurantRef.current = r.data;
      printStationsRef.current = r.data?.print_stations || [];
    }).catch(() => {});
  }, [authReady]);

  const dismissAlert = useCallback(() => {
    setNewOrderAlert(null);
    if (alertIntervalRef.current) {
      clearInterval(alertIntervalRef.current);
      alertIntervalRef.current = null;
    }
    stopBellSound();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, deliverersRes, cashRes, hiddenRes] = await Promise.all([
        authAxiosRef.current.get("/orders?limit=200").catch(e => {
          if (e?.response?.status === 403) console.warn("[ORDERS_FETCH_403]", { token_present: !!localStorage.getItem("token"), token_len: localStorage.getItem("token")?.length || 0 });
          return { data: [] };
        }),
        authAxiosRef.current.get("/deliverers").catch(() => ({ data: [] })),
        authAxiosRef.current.get("/pos/caixa/status").catch(e => {
          if (e?.response?.status === 403) console.warn("[CAIXA_STATUS_403]", { token_present: !!localStorage.getItem("token") });
          return { data: { aberto: false } };
        }),
        authAxiosRef.current.get("/orders?hidden=true&limit=100").catch(() => ({ data: [] })),
      ]);
      // Merge hidden orders that weren't already included in the main 200
      const mainIds = new Set(ordersRes.data.map(o => o.id));
      const extraHidden = hiddenRes.data.filter(o => !mainIds.has(o.id));
      setOrders([...ordersRes.data, ...extraHidden]);
      setDeliverers(deliverersRes.data);
      setCashRegister(cashRes.data?.aberto ? cashRes.data.caixa : null);

      const currentIds = new Set(ordersRes.data.filter(o => o.status === "pending").map(o => o.id));
      if (!isFirstLoad.current) {
        // Auto-dismiss alert if the alerted order is no longer pending (handled on another device)
        setNewOrderAlert(prev => {
          if (prev && !currentIds.has(prev.id)) {
            if (alertIntervalRef.current) { clearInterval(alertIntervalRef.current); alertIntervalRef.current = null; }
            stopBellSound();
            return null;
          }
          return prev;
        });
        const brandNew = [];
        for (const id of currentIds) {
          if (!prevOrderIdsRef.current.has(id)) {
            const order = ordersRes.data.find(o => o.id === id);
            if (order) brandNew.push(order);
          }
        }
        if (brandNew.length > 0) {
          const newest = brandNew[0];
          const orderNum = newest.order_number || newest.id.substring(0, 6).toUpperCase();
          setNewOrderAlert({
            id: newest.id,
            number: orderNum,
            customer: newest.customer_name || "Cliente",
            total: newest.total,
            items: (newest.items || []).length,
            source: newest.source || "",
          });
          playBellSound(3);
          if (alertIntervalRef.current) clearInterval(alertIntervalRef.current);
          alertIntervalRef.current = setInterval(() => playBellSound(2), 4000);
          if (navigator.vibrate) navigator.vibrate([300, 200, 300, 200, 300]);

          const hasAutoPrintStations = printStationsRef.current.some(s => s.auto_print);
          if (hasAutoPrintStations) {
            autoPrintOrder(newest, printStationsRef.current, restaurantRef.current || {});
          }
        }
      }
      isFirstLoad.current = false;
      prevOrderIdsRef.current = currentIds;
      setSelectedOrder(prev => {
        if (!prev) return prev;
        const updated = [...ordersRes.data, ...extraHidden].find(o => o.id === prev.id);
        if (!updated) return null;
        // Close stale detail immediately when another device moves an active order to Ocultos.
        if (updated.hidden && !prev.hidden) return null;
        return updated;
      });
    } catch { toast.error("Error cargando datos"); }
    setLoading(false);
  }, []);

  // Keep ref in sync so polling/WS always call the latest fetchData
  // without causing effect re-runs on authAxios changes.
  fetchDataRef.current = fetchData;

  useEffect(() => {
    if (!authReady) { console.log("[ORDERS_FETCH_BLOCKED_AUTH_NOT_READY]"); return; }
    console.log("[ORDERS_FETCH_START]");
    fetchDataRef.current();
    const interval = setInterval(() => fetchDataRef.current(), 5000);
    // Refresh immediately when tab becomes visible (tablet wakes up / user returns to tab)
    const handleVisibility = () => { if (document.visibilityState === "visible") fetchDataRef.current(); };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", handleVisibility); };
  }, [authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket — instant new-order updates without waiting for poll cycle.
  // Guarded by authReady: only connects after auth bootstrap completes.
  useEffect(() => {
    if (!authReady) { console.log("[KDS_WS_BLOCKED_AUTH_NOT_READY]"); return; }
    let ws = null;
    let retryTimeout = null;

    const getRestaurantId = () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
        if (!token) return null;
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.restaurant_id || null;
      } catch { return null; }
    };

    const connect = () => {
      const rid = getRestaurantId();
      if (!rid) { console.warn("[KDS_WS_CONNECT_FAILED] no restaurant_id in token"); return; }
      const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = window.location.host;
      console.log("[KDS_WS_CONNECT_START]", { rid, token_present: !!token, token_len: token.length, first8: token.slice(0, 8), host });
      try {
        ws = new WebSocket(`${protocol}//${host}/api/ws/kds/${rid}?token=${token}`);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            // Server sends { event: "new_order"|"order_updated"|... } — not msg.type
            if (["new_order", "order_updated", "order_status_updated"].includes(msg.event)) {
              fetchDataRef.current();
            }
          } catch {}
        };
        ws.onclose = (ev) => {
          // code 1006 = abnormal close (server rejected, likely auth failure)
          if (ev.code !== 1000) console.warn("[KDS_WS_AUTH_REJECTED]", { code: ev.code, reason: ev.reason || "no reason given" });
          retryTimeout = setTimeout(connect, 3000);
        };
        ws.onerror = (ev) => { console.warn("[KDS_WS_CONNECT_FAILED]", ev.type); ws.close(); };
      } catch (e) { console.warn("[KDS_WS_CONNECT_FAILED]", e?.message); }
    };

    connect();
    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (ws) ws.close();
    };
  }, [authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { if (alertIntervalRef.current) clearInterval(alertIntervalRef.current); };
  }, []);

  const toggleCaja = async (openingAmount = 0) => {
    try {
      if (cashRegister) {
        setCashRegister(null);
        // Note: actual close is handled by CloseCaixaModal via /pos/caixa/fechar
        // This branch is called after the modal closes successfully
      } else {
        const res = await authAxios.post("/pos/caixa/abrir", { valor_inicial: parseFloat(openingAmount) || 0 });
        setCashRegister(res.data);
        toast.success("Caixa aberto");
        fetchData();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro com o caixa");
    }
  };

  const optimisticHideOrder = (orderId) => {
    setOrders(prev => prev.filter(o => o.id !== orderId));
    setSelectedOrder(null);
  };

  const quickAdvanceStatus = async (orderId, status) => {
    const completesOrder = status === "cancelled" || status === "delivered";
    if (completesOrder) {
      // Completed orders belong in Ocultos; remove immediately while the API persists it.
      setOrders(prev => prev.filter(o => o.id !== orderId));
      setSelectedOrder(prev => prev?.id === orderId ? null : prev);
    }
    try {
      await authAxios.patch(`/orders/${orderId}/status?status=${status}`);
      fetchData();
    } catch {
      if (completesOrder) fetchData(); // Revert optimistic removal on error
      toast.error("Erro ao atualizar");
    }
  };

  const handlePrintAllPending = async () => {
    const pendingOrders = orders.filter(o => ["pending", "preparing"].includes(o.status));
    if (pendingOrders.length === 0) { toast.info("No hay pedidos pendientes para imprimir"); return; }

    const savedMethod = localStorage.getItem("kaiso_print_method");
    if (savedMethod === "desktop") {
      let queued = 0;
      for (const o of pendingOrders) {
        try {
          await authAxios.post("/print/queue", { order_id: o.id, station: "default", copies: 1 });
          queued++;
        } catch { /* skip */ }
      }
      toast.success(`${queued}/${pendingOrders.length} enviados para Kaiso Print`);
      return;
    }

    const { printReceipt } = await import("@/utils/printService");
    const station = printStationsRef.current[0] || {};
    const rest = restaurantRef.current || {};
    let printed = 0;
    for (const o of pendingOrders) {
      const result = await printReceipt(o, station, rest);
      if (result.success) printed++;
    }
    toast.success(`${printed}/${pendingOrders.length} pedidos impresos`);
  };

  const stats = useMemo(() => {
    const today = new Date();
    const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today.toDateString());
    const activeOrders = orders.filter(o => !["delivered", "cancelled"].includes(o.status) && !o.hidden);
    const todayRevenue = todayOrders.filter(o => o.status !== "cancelled").reduce((s, o) => s + (o.total || 0), 0);
    const pending = activeOrders.filter(o => o.status === "pending").length;
    const preparing = activeOrders.filter(o => o.status === "preparing").length;
    const ready = activeOrders.filter(o => o.status === "ready").length;
    const delivering = activeOrders.filter(o => o.status === "delivering").length;
    const todayActive = activeOrders.filter(o => new Date(o.created_at).toDateString() === today.toDateString());
    const avgPrepTime = todayActive.length > 0
      ? Math.round(todayActive.reduce((acc, o) => acc + Math.floor((Date.now() - new Date(o.created_at)) / 60000), 0) / todayActive.length)
      : 0;
    return { todayOrders: todayOrders.length, todayRevenue, pending, preparing, ready, delivering, avgPrepTime, activeTotal: activeOrders.length };
  }, [orders]);

  return {
    orders,
    deliverers,
    cashRegister,
    loading,
    selectedOrder,
    setSelectedOrder,
    newOrderAlert,
    dismissAlert,
    fetchData,
    toggleCaja,
    quickAdvanceStatus,
    optimisticHideOrder,
    handlePrintAllPending,
    stats,
    printStationsRef,
    restaurantRef,
  };
}

